
import React, { useState, useEffect } from "react";
import Main from "../pages/Main";
import AppBreadcrumbs from "./ui/AppBreadcrumbs";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SettlementAccount from "./SettlementAccount";
import MoneyTransfer from "./MoneyTransfer";

import {
  checkSettlementStatusAPI,
  deleteSettlementAccountAPI,
  fetchSettalmentAccounts,
  uploadSettlementDocsAPI,
} from "../api/settalmentApi";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useNotify } from "../context/NotifyContext";
import { verifyBankAccount } from "../api/bankVerificationApi";
import VerifySettlementModal from "./VerifySettlementModal";
import { getStatusTone } from "./listing/StatusBadge";
import CompactSelect from "./listing/CompactSelect";
import { exportTable } from "../utils/downloadExcel";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import useSocketEvent from "../socket/useSocketEvent";

const SETTLEMENT_FILTER_OPTIONS = [
  { value: "account", label: "By Account Number" },
  { value: "name", label: "By Account Holder Name" },
  { value: "all", label: "All Accounts List" },
];
const MoneySettlement = () => {
  const { notify } = useNotify();
  const navigate = useNavigate();

  // =========================================================
  // URL SEARCH PARAMS
  // =========================================================

  const [searchParams, setSearchParams] = useSearchParams();

  const urlFilter = searchParams.get("filter") || "account";
  const urlSearch = searchParams.get("search") || "";
  const urlAction = searchParams.get("action");

  // =========================================================
  // STATES
  // =========================================================

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(null);

  const [searchInput, setSearchInput] = useState(urlSearch);
  const [filterType, setFilterType] = useState(urlFilter);

  const [settalmentsAcc, setSettalmentsAcc] = useState([]);
  const [totalSettalments, setTotalSettalments] = useState(0);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [showAddForm, setShowAddForm] = useState(
    urlAction === "add-account"
  );

  const [showTable, setShowTable] = useState(
    urlFilter === "all" || Boolean(urlSearch)
  );

  const [selectedUser, setSelectedUser] = useState(null);
  const [qrPreviewAccount, setQrPreviewAccount] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyData, setVerifyData] = useState(null);
  const [selectedAcc, setSelectedAcc] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);

  // Existing account list
  const [accountList, setAccountList] = useState([]);

  // =========================================================
  // URL -> STATE SYNC
  // =========================================================

  useEffect(() => {
    // const filter = searchParams.get("filter") || "account";
    const filter = searchParams.get("filter") || "all";
    const search = searchParams.get("search") || "";
    const action = searchParams.get("action");

    setFilterType(filter);
    setSearchInput(search);

    setShowAddForm(action === "add-account");

    setShowTable(
      filter === "all" || Boolean(search)
    );
  }, [searchParams]);

  // =========================================================
  // FETCH SETTLEMENT ACCOUNTS
  // =========================================================

  const fetch_settalmentAcc = async () => {
    const currentFilter =
      searchParams.get("filter") || "account";

    const currentSearch =
      searchParams.get("search") || "";

    const params = {
      page,
      limit,
      startDate,
      endDate,
    };

    // Account Number Search
    if (
      currentFilter === "account" &&
      currentSearch.trim()
    ) {
      params.accountNumber = currentSearch.trim();
    }

    // Account Holder Name Search
    if (
      currentFilter === "name" &&
      currentSearch.trim()
    ) {
      params.accountHolderName = currentSearch.trim();
    }

    try {
      const result = await fetchSettalmentAccounts(params);

      if (result?.success) {
        const response = result?.data;

        setSettalmentsAcc(
          response?.settlements || []
        );

        setTotalPages(
          response?.pagination?.totalPages || 1
        );

        setTotalSettalments(
          response?.pagination?.total || 0
        );
      } else {
        notify(
          result?.message ||
            "Failed to fetch settlements",
          "error"
        );
      }
    } catch (error) {
      notify(
        "Server Error. Please try again later",
        "error"
      );
    }
  };

  // =========================================================
  // FETCH WHEN URL / PAGINATION CHANGES
  // =========================================================

  useEffect(() => {
    fetch_settalmentAcc();
  }, [
    page,
    limit,
    startDate,
    endDate,
    searchParams.toString(),
  ]);

  // =========================================================
  // FILTER CHANGE
  // =========================================================

  const handleFilterChange = (value) => {
    setFilterType(value);
    setSearchInput("");
    setPage(1);

    const params = new URLSearchParams();

    params.set("filter", value);

    // Remove old search
    params.delete("search");

    // Remove add action
    params.delete("action");

    setSearchParams(params);

    setShowAddForm(false);

    // All Accounts -> directly show table
    if (value === "all") {
      setShowTable(true);
    } else {
      setShowTable(false);
    }
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearch = () => {
    const value = searchInput.trim();

    // Search validation
    if (
      filterType !== "all" &&
      !value
    ) {
      notify(
        "Please enter a valid search value!",
        "error"
      );

      return;
    }

    setPage(1);

    const params = new URLSearchParams();

    // Save filter in URL
    params.set(
      "filter",
      filterType
    );

    // Save search in URL
    if (
      filterType !== "all" &&
      value
    ) {
      params.set(
        "search",
        value
      );
    }

    // Search karte waqt Add form remove
    params.delete("action");

    setSearchParams(params);

    setShowAddForm(false);
    setShowTable(true);
  };

  // =========================================================
  // CLEAR FILTER
  // =========================================================

  const handleClear = () => {
    setFilterType("account");
    setSearchInput("");
    setShowTable(false);
    setShowAddForm(false);
    setPage(1);

    // Completely clean URL
    setSearchParams({});
  };

  const handleExportExcel = () => {
    exportTable(
      "settlement-accounts",
      [
        { label: "SERIAL NO.", value: (_r, i) => (page - 1) * limit + i + 1 },
        { label: "ACCOUNT HOLDER NAME", key: "accountHolderName" },
        { label: "ACCOUNT NUMBER", key: "accountNumber" },
        { label: "IFSC CODE", key: "ifsc" },
        { label: "BANK NAME", key: "bankName" },
        { label: "VERIFY STATUS", key: "status" },
      ],
      settalmentsAcc,
      notify
    );
  };

  // =========================================================
  // OPEN ADD ACCOUNT - URL BASED
  // =========================================================

  const handleAddAccount = () => {
    const params = new URLSearchParams(
      searchParams
    );

    params.set(
      "action",
      "add-account"
    );

    setSearchParams(params);

    setShowAddForm(true);
  };

  // =========================================================
  // CLOSE ADD ACCOUNT
  // =========================================================

  const CloseForm = () => {
    const params = new URLSearchParams(
      searchParams
    );

    // Only remove action
    // Existing filter/search remain
    params.delete("action");

    setSearchParams(params);

    setShowAddForm(false);

    // Refresh account list
    fetch_settalmentAcc();
  };

  // =========================================================
  // DELETE HANDLERS
  // =========================================================

  const handleDelete = (item) => {
    setDeleteTarget(item);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
  };

  // const confirmDelete = async () => {
  //   if (!deleteTarget) return;

  //   try {
  //     const res =
  //       await deleteSettlementAccountAPI(
  //         deleteTarget?._id
  //       );

  //     if (res.success) {
  //       setSettalmentsAcc(
  //         settalmentsAcc.filter(
  //           (acc) =>
  //             acc.id !==
  //             deleteTarget.id
  //         )
  //       );

  //       notify(
  //         "Account deleted successfully!",
  //         "success"
  //       );

  //       setDeleteTarget(null);
  //     } else {
  //       notify(
  //         res.message,
  //         "error"
  //       );
  //     }
  //   } catch (err) {
  //     console.error(err);

  //     notify(
  //       "Server error. Please try again.",
  //       "error"
  //     );
  //   }
  // };

  // =========================================================
  // TOGGLE STATUS
  // =========================================================

  const confirmDelete = async () => {
  if (!deleteTarget) return;

  try {
    const res = await deleteSettlementAccountAPI(deleteTarget?._id);

    if (res?.success) {
      // Remove only deleted account from current table
      setSettalmentsAcc((prev) =>
        prev.filter((acc) => acc?._id !== deleteTarget?._id)
      );

      notify("Account deleted successfully!", "success");

      setDeleteTarget(null);

      // IMPORTANT:
      // URL filter/search ko touch nahi karna hai.
      // Current filter/search same rahega.
    } else {
      notify(
        res?.message || "Failed to delete account",
        "error"
      );
    }
  } catch (err) {
    console.error(err);

    notify(
      "Server error. Please try again.",
      "error"
    );
  }
};

  const toggleStatus = (index) => {
    if (
      index < 0 ||
      index >= accountList.length
    ) {
      return;
    }

    const updatedList = [
      ...accountList,
    ];

    updatedList[index].active =
      !updatedList[index].active;

    setAccountList(updatedList);

    notify(
      `Account ${
        updatedList[index].active
          ? "activated"
          : "deactivated"
      } successfully`,
      "info"
    );
  };

  // =========================================================
  // VERIFY ACCOUNT (PENNY DROP / BANK VERIFICATION)
  // =========================================================

  const handleVerify = async (item) => {
    if (!item?._id) return;
    try {
      setVerifyingId(item._id);
      const res = await verifyBankAccount({
        sourceType: "SETTLEMENT",
        sourceId: item._id,
        bankId: item.bankId || "",
      });

      if (res.success) {
        notify(res.message || "Bank account verified successfully", "success");
      } else {
        notify(res.message || "Bank verification failed", "error");
      }
      fetch_settalmentAcc();
    } catch (err) {
      notify(
        err?.response?.data?.message ||
          "Server Error",
        "error"
      );
    } finally {
      setVerifyingId(null);
    }
  };

  // =========================================================
  // UPLOAD DOCUMENTS
  // =========================================================

  const uploadDocs = async (
    pan,
    cheque
  ) => {
    try {
      const fd = new FormData();

      fd.append(
        "settlementId",
        selectedAcc._id
      );

      fd.append(
        "panFile",
        pan
      );

      fd.append(
        "chequeFile",
        cheque
      );

      const res =
        await uploadSettlementDocsAPI(
          fd
        );

      if (res.success) {
        notify(
          res.message,
          "success"
        );

        setVerifyOpen(false);

        fetch_settalmentAcc();
      } else {
        notify(
          res.message,
          "error"
        );
      }
    } catch (err) {
      console.log(err);

      notify(
        err?.response?.data?.message ||
          "Upload Failed",
        "error"
      );
    }
  };

  useSocketEvent("settlement:account_updated", (payload) => {
  if (!payload?.settlementId) return;

  setSettalmentsAcc((prev) =>
    prev.map((item) => {
      if (
        String(item?._id) !==
        String(payload.settlementId)
      ) {
        return item;
      }

      return {
        ...item,

        status:
          payload.status ??
          item.status,

        verified:
          payload.verified ??
          item.verified,

        isActive:
          payload.isActive ??
          item.isActive,

        upiId:
          payload.upiId ??
          item.upiId,

        upiVerified:
          payload.upiVerified ??
          item.upiVerified,
      };
    })
  );
});

return (
  <Main showRightPanel={false}>

    <div className="mt-4 font-[Poppins]">

      {/* =====================================================
          BREADCRUMBS
      ===================================================== */}

      <AppBreadcrumbs
        items={[
          {
            label: "Dashboard",
            to: "/",
          },

          {
            label: "MATM Module",
          },

          {
            label: "Settlements",
          },

          {
            label: showAddForm
              ? "Add New Account Details"
              : selectedUser
                ? "Bank Transfer"
                : "Find Account Details",
          },
        ]}
      />

      {/* =====================================================
          ADD ACCOUNT FORM
      ===================================================== */}

      {showAddForm ? (

        <SettlementAccount
          open={showAddForm}
          close={CloseForm}
        />

      ) : selectedUser ? (

        <MoneyTransfer
          selectedUser={selectedUser}
          onCancel={() =>
            setSelectedUser(null)
          }
        />

      ) : (

        <div className="ds-card p-4 flex flex-col gap-4 mt-4">

          {/* =================================================
              DESKTOP
          ================================================= */}

          <div className="hidden lg:flex flex-col gap-3 w-full">

            {/* ---------------------------------------------
                ROW 1
                Select + Input + Search
            --------------------------------------------- */}

            <div className="flex items-center gap-3 w-full">

              {/* Filter */}
              <label className="ds-listing-field">
                {/* <span className="ds-listing-field__label">Filter</span> */}
                <CompactSelect
                  className="ds-filter-status-select"
                  value={filterType}
                  onChange={handleFilterChange}
                  options={SETTLEMENT_FILTER_OPTIONS}
                  placeholder="Filter"
                  ariaLabel="Filter"
                  searchable={false}
                />
              </label>

              {/* Search Input */}
              <input
              size="small"
                type={
                  filterType === "account"
                    ? "number"
                    : "text"
                }

                placeholder={
                  filterType === "account"
                    ? "Enter Account Number"
                    : filterType === "name"
                      ? "Enter Account Holder Name"
                      : "Input disabled"
                }

                disabled={
                  filterType === "all"
                }

                value={searchInput}

                onChange={(e) =>
                  setSearchInput(
                    e.target.value
                  )
                }

                onKeyDown={(e) => {
                  if (
                    e.key === "Enter"
                  ) {
                    handleSearch();
                  }
                }}

                className="ds-input w-[420px] text-sm"
              />

              {/* Search */}
              <button
                type="button"
                className="ds-btn ds-btn-secondary whitespace-nowrap"
                style={{
                  color:
                    "var(--warning-color)",
                }}
                onClick={
                  handleSearch
                }
              >

                <SearchRoundedIcon
                  fontSize="small"
                />

                SEARCH

              </button>

            </div>


            {/* ---------------------------------------------
                ROW 2
                RIGHT SIDE ACTIONS
            --------------------------------------------- */}

            <div className="flex items-center justify-end gap-3 w-full">

              {/* Add Account */}
              <button
                type="button"
                onClick={
                  handleAddAccount
                }
                className="ds-btn ds-btn-ghost border border-[var(--border-color)] whitespace-nowrap"
              >

                <AddRoundedIcon
                  fontSize="small"
                />

                ADD NEW ACCOUNT DETAILS

              </button>


              {/* Export */}
              <button
                type="button"
                className="ds-action-btn ds-filter-export"
                onClick={handleExportExcel}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
                EXPORT TO EXCEL
              </button>


              {/* Clear */}
              <button
                type="button"
                onClick={
                  handleClear
                }
                className="ds-btn ds-btn-ghost whitespace-nowrap"
                style={{
                  color:
                    "var(--danger-color)",

                  border:
                    "1px solid var(--danger-color)",
                }}
              >

                CLEAR

              </button>

            </div>

          </div>


          {/* =================================================
              MOBILE
          ================================================= */}

          <div className="flex lg:hidden flex-col gap-3 w-full">

            {/* ---------------------------------------------
                ROW 1
                Select + Clear
            --------------------------------------------- */}

            <div className="flex items-center gap-2 w-full">

              <label className="ds-listing-field flex-1 min-w-0">
                <span className="ds-listing-field__label">Filter</span>
                <CompactSelect
                  className="ds-filter-status-select"
                  value={filterType}
                  onChange={handleFilterChange}
                  options={SETTLEMENT_FILTER_OPTIONS}
                  placeholder="Filter"
                  ariaLabel="Filter"
                  searchable={false}
                />
              </label>


              <button
                type="button"
                onClick={
                  handleClear
                }
                className="ds-btn ds-btn-ghost px-3"
                style={{
                  color:
                    "var(--danger-color)",

                  border:
                    "1px solid var(--danger-color)",
                }}
              >

                CLEAR

              </button>

            </div>


            {/* ---------------------------------------------
                ROW 2
                Input + Search Icon
            --------------------------------------------- */}

            <div className="flex items-center gap-2 w-full">

              <input
                type={
                  filterType === "account"
                    ? "number"
                    : "text"
                }

                placeholder={
                  filterType === "account"
                    ? "Enter Account Number"
                    : filterType === "name"
                      ? "Enter Account Holder Name"
                      : "Input disabled"
                }

                disabled={
                  filterType === "all"
                }

                value={searchInput}

                onChange={(e) =>
                  setSearchInput(
                    e.target.value
                  )
                }

                onKeyDown={(e) => {
                  if (
                    e.key === "Enter"
                  ) {
                    handleSearch();
                  }
                }}

                className="ds-input flex-1 min-w-0 text-sm"
              />


              <button
                type="button"
                onClick={
                  handleSearch
                }
                aria-label="Search"
                className="ds-btn ds-btn-secondary !px-3 !min-w-[42px]"
                style={{
                  color:
                    "var(--warning-color)",
                }}
              >

                <SearchRoundedIcon
                  fontSize="small"
                />

              </button>

            </div>


            {/* ---------------------------------------------
                ROW 3
                Excel + Add Account
            --------------------------------------------- */}

            <div className="grid grid-cols-2 gap-2 w-full">

              {/* Excel */}
              <button
                type="button"
                className="ds-action-btn ds-filter-export w-full justify-center"
                onClick={handleExportExcel}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
                EXPORT TO EXCEL
              </button>


              {/* Add Account */}
              <button
                type="button"
                onClick={
                  handleAddAccount
                }
                className="ds-btn ds-btn-ghost w-full justify-center text-xs border border-[var(--border-color)]"
              >

                <AddRoundedIcon
                  fontSize="small"
                />

                ADD ACCOUNT

              </button>

            </div>

          </div>


          {/* =================================================
              TABLE
          ================================================= */}

          {showTable && (

            <div className="overflow-auto mt-2">

              <table className="w-full border-collapse text-sm whitespace-nowrap">

                <thead>

                  <tr className="bg-[#005f8f] text-white text-left whitespace-nowrap">

                    <th className="px-3 py-2">
                      SERIAL NO.
                    </th>

                    <th className="px-3 py-2">
                      ACCOUNT HOLDER NAME
                    </th>

                    <th className="px-3 py-2">
                      ACCOUNT NUMBER
                    </th>

                    <th className="px-3 py-2">
                      IFSC CODE
                    </th>

                    <th className="px-3 py-2">
                      BANK NAME
                    </th>
<th className="px-3 py-2">
  UPI ID
</th>

<th className="px-3 py-2">
  QR CODE
</th>

<th className="px-3 py-2">
  UPI STATUS
</th>
                    <th className="px-3 py-2">
                      VERIFY STATUS
                    </th>

                    <th className="px-3 py-2">
                      OPERATION
                    </th>

                  </tr>

                </thead>


               <tbody>
  {settalmentsAcc?.length > 0 ? (
    settalmentsAcc.map((item, index) => (
      <tr
        key={item?.beneId || index}
        className="border-b"
      >
        <td className="px-3 py-2 font-semibold">
          {(page - 1) * limit + index + 1}
        </td>

        <td
          className="px-3 py-2 text-[#0056c1] font-semibold cursor-pointer underline hover:text-blue-700"
          onClick={() =>
            navigate(
              `/settalment-bank/${item?._id}`
            )
          }
        >
          {item.accountHolderName}
        
        </td>

        <td className="px-3 py-2">
          {item.accountNumber}
        </td>

        <td className="px-3 py-2">
          {item.ifsc}
        </td>

        <td className="px-3 py-2">
          {item.bankName}
        </td>
        <td className="px-3 py-2">
  {item?.upiId || "--"}
</td>

<td className="px-2 py-2 text-center">
  {item?.upiId ? (
    <span
      className={`ds-status ds-status-${
        item?.upiVerified ? "success" : "warning"
      }`}
    >
      {item?.upiVerified ? "Verified" : "Unverified"}
    </span>
  ) : (
    <span className="text-xs text-[var(--text-muted)]">
      --
    </span>
  )}
</td>

<td className="px-2 py-2 text-center">
  {item?.qrCodeUrl ? (
    <button
      type="button"
      onClick={() => setQrPreviewAccount(item)}
      title="View QR Code"
      aria-label={`View QR Code for ${item?.accountHolderName || "settlement account"}`}
      className="mx-auto flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-[var(--border-color)] bg-white"
    >
      <img
        src={item.qrCodeUrl}
        alt="QR preview"
        className="h-full w-full object-contain"
      />
    </button>
  ) : (
    <span className="text-xs text-[var(--text-muted)]">--</span>
  )}
</td>

        
<td className="px-2 py-2 text-center">
  {String(item?.status || "").toUpperCase() === "VERIFIED" ? (
    <span className="ds-status ds-status-success font-semibold">
      Bank Verified
    </span>
  ) : item?.verificationBypassed ? (
    <span className="ds-status ds-status-info font-semibold">
      Verification Bypassed
    </span>
  ) : String(item?.status || "").toUpperCase() === "PENDING" || verifyingId === item._id ? (
    <span className="ds-status ds-status-warning font-semibold">
      {verifyingId === item._id ? "Verifying…" : "Verification Pending"}
    </span>
  ) : String(item?.status || "").toUpperCase() === "FAILED" ? (
    <button
      type="button"
      onClick={() => handleVerify(item)}
      disabled={verifyingId === item._id}
      className="ds-status ds-status-danger cursor-pointer font-semibold"
    >
      Failed — Retry
    </button>
  ) : (
    <button
      type="button"
      onClick={() => handleVerify(item)}
      disabled={verifyingId === item._id}
      className="ds-status ds-status-warning cursor-pointer font-semibold hover:opacity-80"
    >
      Verify Bank
    </button>
  )}
</td>


        <td className="px-2 py-2 text-center">
          <button
            type="button"
            className="bg-[#ff000026] text-[#ff0000] hover:bg-[#ff000043] px-3 py-1.5 rounded-lg text-xs"
            onClick={() => handleDelete(item)}
          >
            Delete Account
          </button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td
        colSpan={10}
        className="px-4 py-10 text-center"
      >
        <div className="flex flex-col items-center justify-center gap-1">
          <span className="text-sm font-semibold text-[var(--text-dark)]">
            No Account Found
          </span>

          <span className="text-xs text-[var(--text-muted)]">
            No account details match your search.
          </span>
        </div>
      </td>
    </tr>
  )}
</tbody>

              </table>

            </div>

          )}


          {/* Bottom Text */}
          <p className="text-[10px] sm:text-[14px] font-medium text-[var(--text-muted)]">

            Accounts list will be available here
            for the searched name and account number.

          </p>

        </div>

      )}


      {/* =====================================================
          DELETE CONFIRMATION
      ===================================================== */}

      {deleteTarget && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

          <div className="ds-card p-6 w-80 max-w-sm text-center">

            <h2 className="text-lg font-bold mb-4 text-[var(--text-dark)]">
              Delete Account Confirmation
            </h2>

            <p className="mb-6 text-[var(--text-muted)]">

              Are you sure ??<br />

              Want to delete account details
              with serial number -{" "}

              {deleteTarget.serial}

            </p>


            <div className="flex justify-center gap-4">

              <button
                type="button"
                className="ds-btn ds-btn-secondary"
                onClick={
                  cancelDelete
                }
              >

                Cancel

              </button>


              <button
                type="button"
                className="ds-btn ds-btn-primary"
                style={{
                  backgroundColor:
                    "var(--error-color)",

                  color: "#fff",
                }}
                onClick={
                  confirmDelete
                }
              >

                Yes, Delete

              </button>

            </div>

          </div>

        </div>

      )}


      {qrPreviewAccount && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setQrPreviewAccount(null)}
        >
          <div
            className="ds-card w-full max-w-lg p-4"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="m-0 text-base font-semibold text-[var(--text-dark)]">
                Settlement QR Code
              </h2>
              <button
                type="button"
                onClick={() => setQrPreviewAccount(null)}
                className="ds-btn ds-btn-secondary"
              >
                Close
              </button>
            </div>
            <div className="flex max-h-[70vh] justify-center overflow-auto rounded border border-[var(--border-color)] bg-white p-3">
              <img
                src={qrPreviewAccount.qrCodeUrl}
                alt={`QR Code for ${qrPreviewAccount.accountHolderName || "settlement account"}`}
                className="max-h-[65vh] max-w-full object-contain"
              />
            </div>
            <p className="mb-0 mt-3 text-sm font-medium text-[var(--text-dark)]">
              {qrPreviewAccount.accountHolderName || "Settlement account"}
            </p>
          </div>
        </div>
      )}


      {/* =====================================================
          VERIFY MODAL
      ===================================================== */}

      <VerifySettlementModal
        open={verifyOpen}
        onClose={() =>
          setVerifyOpen(false)
        }
        data={verifyData}
        acc={selectedAcc}
        onUpload={uploadDocs}
      />

    </div>

  </Main>
);

};
export default MoneySettlement;